import React from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import {
  Form,
  PrimaryButton,
  FieldTextInput,
  NamedLink,
  SocialLoginButton,
} from '../../components';
import * as validators from '../../util/validators';
import { apiBaseUrl } from '../../util/api';
import { FacebookLogo, GoogleLogo } from '../../containers/AuthenticationPage/socialLoginLogos';

import css from './LoginForm.css';

const LoginFormComponent = props => (
  <FinalForm
    {...props}
    render={fieldRenderProps => {
      const {
        rootClassName,
        className,
        formId,
        handleSubmit,
        inProgress,
        intl,
        invalid,
      } = fieldRenderProps;

      const emailLabel = intl.formatMessage({
        id: 'LoginForm.emailLabel',
      });
      const emailPlaceholder = intl.formatMessage({
        id: 'LoginForm.emailPlaceholder',
      });
      const emailRequiredMessage = intl.formatMessage({
        id: 'LoginForm.emailRequired',
      });
      const emailRequired = validators.required(emailRequiredMessage);
      const emailInvalidMessage = intl.formatMessage({
        id: 'LoginForm.emailInvalid',
      });
      const emailValid = validators.emailFormatValid(emailInvalidMessage);

      const passwordLabel = intl.formatMessage({
        id: 'LoginForm.passwordLabel',
      });
      const passwordPlaceholder = intl.formatMessage({
        id: 'LoginForm.passwordPlaceholder',
      });
      const passwordRequiredMessage = intl.formatMessage({
        id: 'LoginForm.passwordRequired',
      });
      const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);

      const classes = classNames(rootClassName || css.root, className);
      const submitInProgress = inProgress;
      const submitDisabled = invalid || submitInProgress;

      const passwordRecoveryLink = (
        <NamedLink name="PasswordRecoveryPage" className={css.recoveryLink}>
          <FormattedMessage id="LoginForm.forgotPassword" />
        </NamedLink>
      );

      const showFacebookLogin = !!process.env.REACT_APP_FACEBOOK_APP_ID;
      const showGoogleLogin = !!process.env.REACT_APP_GOOGLE_CLIENT_ID;
      const showSocialLogins = showFacebookLogin || showGoogleLogin;

      const authWithIdp = idp => () => {
        if (typeof window === 'undefined') return;
        const baseUrl = apiBaseUrl();
        window.location.href = `${baseUrl}/api/auth/${idp}`;
      };

      const socialLoginButtonsMaybe = showSocialLogins ? (
        <div className={css.idpButtons}>
          <div className={css.socialButtonsOr}>
            <span className={css.socialButtonsOrText}>
              <FormattedMessage id="AuthenticationPage.or" />
            </span>
          </div>

          {showFacebookLogin ? (
            <div className={css.socialButtonWrapper}>
              <SocialLoginButton onClick={authWithIdp('facebook')}>
                <span className={css.buttonIcon}>{FacebookLogo}</span>
                <FormattedMessage id="AuthenticationPage.loginWithFacebook" />
              </SocialLoginButton>
            </div>
          ) : null}

          {showGoogleLogin ? (
            <div className={css.socialButtonWrapper}>
              <SocialLoginButton onClick={authWithIdp('google')}>
                <span className={css.buttonIcon}>{GoogleLogo}</span>
                <FormattedMessage id="AuthenticationPage.loginWithGoogle" />
              </SocialLoginButton>
            </div>
          ) : null}
        </div>
      ) : null;

      return (
        <Form className={classes} onSubmit={handleSubmit}>
          <div className={css.fields}>
            <FieldTextInput
              type="email"
              id={formId ? `${formId}.email` : 'email'}
              name="email"
              autoComplete="email"
              label={emailLabel}
              placeholder={emailPlaceholder}
              validate={validators.composeValidators(emailRequired, emailValid)}
            />
            <FieldTextInput
              className={css.password}
              type="password"
              id={formId ? `${formId}.password` : 'password'}
              name="password"
              autoComplete="current-password"
              label={passwordLabel}
              placeholder={passwordPlaceholder}
              validate={passwordRequired}
            />
          </div>
          <div className={css.bottomWrapper}>
            <PrimaryButton type="submit" inProgress={submitInProgress} disabled={submitDisabled}>
              <FormattedMessage id="LoginForm.logIn" />
            </PrimaryButton>
            <p className={css.bottomWrapperText}>
              <span className={css.recoveryLinkInfo}>
                <FormattedMessage
                  id="LoginForm.forgotPasswordInfo"
                  values={{ passwordRecoveryLink }}
                />
              </span>
            </p>
          </div>
          {socialLoginButtonsMaybe}
        </Form>
      );
    }}
  />
);

LoginFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  form: null,
  inProgress: false,
};

const { string, bool } = PropTypes;

LoginFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  form: string,
  inProgress: bool,
  intl: intlShape.isRequired,
};

const LoginForm = compose(injectIntl)(LoginFormComponent);
LoginForm.displayName = 'LoginForm';

export default LoginForm;
